// This is a COPY of downread/shared/src/utils/post-stats-badges.ts. To modify, edit the original and then run downread/bin/build-shared.sh
import { PostStats, PostTextBadge, PostTextType } from './feed-schema';

/** Words per post above which a feed carries whole articles rather than teasers. */
export const FULL_TEXT_WORDS = 120;
/** Words per post above which the articles count as long-form reading. */
export const LONG_FORM_WORDS = 1000;
/** Videos / audio items per post above which most posts carry one. */
export const MEDIA_FEED_THRESHOLD = 0.5;
/** Share of posts linking to a foreign site above which a feed is a link blog (Hacker News). */
export const LINK_FEED_THRESHOLD = 0.5;
/** Share of titled posts below which a short-post feed is a microblog rather than summaries. */
export const MICROBLOG_TITLE_SHARE = 0.25;
/** Words per post below which titled posts are no summaries (comics, photos, bare links). */
export const MIN_SUMMARY_WORDS = 10;
/** Categories whose posts are pictures: what little text they carry says nothing about them. */
const PICTURE_CATEGORIES = ['comics', 'photography'];

/** The one badge the stats suggest for a feed's text, or none. */
function detectTextBadge(stats: PostStats, category: string | undefined, links: boolean): PostTextBadge | undefined {
    if (category && PICTURE_CATEGORIES.includes(category)) return undefined;
    if (stats.words > LONG_FORM_WORDS) return 'long posts';
    if (stats.words > FULL_TEXT_WORDS) return 'full posts';
    // Stats from before titles were counted have no short-text badge until the next fetch.
    if (stats.titles == undefined) return undefined;
    if (stats.titles < MICROBLOG_TITLE_SHARE) return 'micro blog';
    // A link blog's short posts are just the link, and a handful of words (a comic, a photo) are
    // no summary either.
    return !links && stats.words >= MIN_SUMMARY_WORDS ? 'summaries' : undefined;
}

const hasLinks = (stats: PostStats) => (stats.links ?? 0) > LINK_FEED_THRESHOLD;

/**
 * The one badge describing a feed's text, or undefined. An admin's catalog override (textType)
 * beats the detection, which can't tell long summaries (Ars Technica) from full posts; 'none'
 * suppresses the text badge.
 */
export function postTextBadge(stats: PostStats | undefined, category?: string, textType?: PostTextType): PostTextBadge | undefined {
    if (textType) return textType == 'none' ? undefined : textType;
    return stats && detectTextBadge(stats, category, hasLinks(stats));
}

/**
 * Content badges for a feed (the feed tile and the home page's feed types), derived from the sync server's approximate per-post averages
 * (RestUserFeed.postStats). Text gets at most one badge: 'long posts' / 'full posts' by length, or
 * for short posts 'micro blog' (mostly untitled) / 'summaries' (titled, 10+ words, not a link blog).
 * Comics and photography carry no text badge at all. Links, videos and audio get one each. Empty
 * when the feed has not been analyzed yet or nothing crosses a threshold. textType is the catalog's
 * admin override of the text badge (see postTextBadge).
 */
export function postStatsBadges(stats: PostStats | undefined, category?: string, textType?: PostTextType): string[] {
    const text = postTextBadge(stats, category, textType);
    const badges: string[] = text ? [text] : [];
    if (!stats) return badges;
    const links = hasLinks(stats);
    if (links) badges.push('links');
    if (stats.videos > MEDIA_FEED_THRESHOLD) badges.push('videos');
    if (stats.audios > MEDIA_FEED_THRESHOLD) badges.push('audio');
    return badges;
}
